import Card, { CardHeader } from '../ui/Card';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vermieterApi } from '../../api/vermieter';
import EntityLinkButton from '../ui/EntityLinkButton';
import { useIsModuleEnabled } from '../../hooks/useConfigVisibility';
import type { Vermieter } from '../../types/index';

interface VermieterPickerProps {
  current: { id: number; name: string; typ: string | null } | null;
  onAssign: (vermieter: Vermieter) => Promise<unknown>;
  onUnlink: () => Promise<unknown>;
  /** Quick-create suggestion pills (used by VerfahrenDetail from linked Inserate host names) */
  suggestions?: { name: string; typ: string }[];
  /** Scraped host data hint shown when nothing is linked (used by InseratDetail) */
  hostHint?: { name: string | null; typ: string | null };
  isPending?: boolean;
  /** Content shown in body when linked+view mode (e.g. a state suggestion banner) */
  linkedExtra?: React.ReactNode;
  /** Hint text shown when nothing is linked yet */
  unlinkedHint?: string;
  /** For listing flow: keep create form open while unlinked, prefilled with these values. */
  forceCreateWhenUnlinked?: boolean;
  createPrefill?: { name?: string | null; typ?: string | null };
}

type Mode = 'view' | 'search' | 'create';

export default function VermieterPicker({
  current,
  onAssign,
  onUnlink,
  suggestions,
  hostHint,
  isPending,
  linkedExtra,
  unlinkedHint = 'Noch kein Vermieter zugeordnet.',
  forceCreateWhenUnlinked = false,
  createPrefill,
}: VermieterPickerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const vermieterVisible = useIsModuleEnabled('vermieter');
  const [mode, setMode] = useState<Mode>(() => (forceCreateWhenUnlinked && !current ? 'create' : 'view'));
  const [searchText, setSearchText] = useState('');
  const [newName, setNewName] = useState(forceCreateWhenUnlinked && !current ? (createPrefill?.name ?? '') : '');
  const [newTyp, setNewTyp] = useState(forceCreateWhenUnlinked && !current ? (createPrefill?.typ ?? '') : '');
  const [newEmail, setNewEmail] = useState('');
  const [newTelefon, setNewTelefon] = useState('');
  const [suppressAutoCreateUntilLinked, setSuppressAutoCreateUntilLinked] = useState(false);

  const { data: searchResults } = useQuery({
    queryKey: ['vermieter', 'search', searchText],
    queryFn: () => vermieterApi.getVermieter({ search_text: searchText, per_page: 10 }),
    enabled: mode === 'search' && searchText.length >= 1,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      vermieterApi.createVermieter({
        name: newName.trim(),
        typ: newTyp || undefined,
        email: newEmail.trim() || undefined,
        telefon: newTelefon.trim() || undefined,
      }),
    onSuccess: async (created) => {
      setSuppressAutoCreateUntilLinked(true);
      queryClient.invalidateQueries({ queryKey: ['vermieter'] });
      await onAssign(created);
      reset();
    },
  });

  const handleSelect = async (v: Vermieter) => {
    setSuppressAutoCreateUntilLinked(true);
    await onAssign(v);
    reset();
  };

  useEffect(() => {
    if (!current && forceCreateWhenUnlinked && mode === 'view' && !suppressAutoCreateUntilLinked) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Enter create mode when the parent reports an unlinked required landlord.
      setNewName(createPrefill?.name ?? '');
      setNewTyp(createPrefill?.typ ?? '');
      setMode('create');
    }
  }, [
    current,
    forceCreateWhenUnlinked,
    mode,
    suppressAutoCreateUntilLinked,
    createPrefill?.name,
    createPrefill?.typ,
  ]);

  useEffect(() => {
    if (current && suppressAutoCreateUntilLinked) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Re-enable auto-create after the parent confirms the link.
      setSuppressAutoCreateUntilLinked(false);
    }
  }, [current, suppressAutoCreateUntilLinked]);

  const reset = () => {
    setMode('view');
    setSearchText('');
    setNewName('');
    setNewTyp('');
    setNewEmail('');
    setNewTelefon('');
  };

  const startCreate = (prefillName = '', prefillTyp = '') => {
    setNewName(prefillName);
    setNewTyp(prefillTyp);
    setMode('create');
  };

  const inputClass =
    'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  // Body is visible when there is something meaningful to show:
  // - user is actively editing (search or create mode)
  // - nothing is linked yet (show hint + action buttons)
  // - linked with extra content to display
  const showBody = mode !== 'view' || !current || !!linkedExtra;

  // Vermieter is a feature-gated page; hide the picker entirely when disabled.
  if (!vermieterVisible) return null;

  return (
    <Card>
      {/* Header */}
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-medium">Vermieter</h3>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
              current ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${current ? 'bg-teal-500' : 'bg-gray-400'}`} />
            {current ? 'Zugeordnet' : 'Nicht zugeordnet'}
          </span>
          {current && mode === 'view' && (
            <EntityLinkButton
              label={`${current.name}${current.typ ? ` (${current.typ})` : ''}`}
              onClick={() => navigate(`/vermieter/${current.id}`)}
              color="teal"
            />
          )}
        </div>
        {current && mode === 'view' ? (
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setMode('search')} className="btn btn-text text-sm">
              Ändern
            </button>
            <button
              onClick={onUnlink}
              disabled={isPending}
              className="btn btn-text text-danger-700 enabled:hover:text-danger-900 focus-visible:ring-danger-500 text-sm"
            >
              Trennen
            </button>
          </div>
        ) : mode !== 'view' ? (
          <button onClick={reset} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 shrink-0 text-sm">
            Abbrechen
          </button>
        ) : null}
      </CardHeader>

      {/* Body */}
      {showBody && (
        <div className="px-6 py-4">
          {mode === 'search' ? (
            <div className="space-y-3">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Name, E-Mail suchen..."
                autoFocus
                className={inputClass}
              />
              {searchResults && searchResults.items.length > 0 && (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {searchResults.items.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => handleSelect(v)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-teal-50 transition-colors flex items-center justify-between"
                    >
                      <span className="font-medium">{v.name}</span>
                      <span className="text-xs text-gray-400">
                        {v.typ && <span className="mr-2">{v.typ}</span>}
                        {v.inserate_count} Inserate
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {searchText && searchResults && searchResults.items.length === 0 && (
                <p className="text-xs text-gray-400">Keine Vermieter gefunden.</p>
              )}
              <button
                onClick={() => startCreate(searchText)}
                className="btn btn-text text-teal-700 enabled:hover:text-teal-900 focus-visible:ring-teal-500 text-sm"
              >
                &laquo;{searchText || 'Neuen'}&raquo; erstellen
              </button>
            </div>
          ) : mode === 'create' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Name des Vermieters"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Typ (optional)</label>
                  <select value={newTyp} onChange={(e) => setNewTyp(e.target.value)} className={inputClass}>
                    <option value="">-- kein Typ --</option>
                    <option value="privatperson">Privatperson</option>
                    <option value="gewerblich">Gewerblich</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">E-Mail (optional)</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Nicht angegeben"
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Telefon (optional)</label>
                  <input
                    type="text"
                    value={newTelefon}
                    onChange={(e) => setNewTelefon(e.target.value)}
                    placeholder="Nicht angegeben"
                    className={inputClass}
                  />
                </div>
              </div>
              {createMutation.isError && (
                <p className="text-sm text-red-600">Fehler beim Erstellen.</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => createMutation.mutate()}
                  disabled={!newName.trim() || createMutation.isPending}
                  className="btn btn-primary bg-teal-600 enabled:hover:bg-teal-700 focus-visible:ring-teal-500"
                >
                  {createMutation.isPending ? 'Wird erstellt...' : 'Erstellen & Zuordnen'}
                </button>
                <button
                  onClick={() => setMode('search')}
                  className="btn btn-secondary"
                >
                  Zurück
                </button>
              </div>
            </div>
          ) : current ? (
            /* linked + view: only shows if linkedExtra was provided */
            linkedExtra
          ) : suppressAutoCreateUntilLinked ? (
            <p className="text-sm text-gray-500">Vermieter wurde erstellt. Zuordnung wird aktualisiert...</p>
          ) : (
            /* not linked + view: show hints and action buttons */
            <div className="space-y-3">
              {/* Vermieter-Vorschlag deaktiviert: gescrapte Host-Daten stiften hier
                  keinen Mehrwert (oft kein Name gefunden, Typ nur heuristisch, Label
                  "(Aktualisierung)" irreführend). Bei Bedarf reaktivieren:
              {hostHint && (hostHint.name || hostHint.typ) && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                  <p className="text-xs font-medium text-blue-700 mb-1">Gefundene Daten (Aktualisierung)</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-blue-800">
                    {hostHint.name && <span>{hostHint.name}</span>}
                    {hostHint.typ && <span>Typ: {hostHint.typ}</span>}
                  </div>
                </div>
              )} */}
              {suggestions && suggestions.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Aus Inseraten übernehmen</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s.name}
                        onClick={() => startCreate(s.name, s.typ)}
                        className="btn btn-secondary bg-teal-50 text-teal-700 border-teal-200 enabled:hover:bg-teal-100 focus-visible:ring-teal-500 px-3 py-1.5 border"
                      >
                        {s.name}
                        {s.typ && <span className="text-xs text-teal-600 ml-1">({s.typ})</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-gray-500">{unlinkedHint}</p>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setMode('search')}
                    disabled={suppressAutoCreateUntilLinked}
                    className="btn btn-secondary"
                  >
                    Vorhandenen suchen
                  </button>
                  <button
                    onClick={() => startCreate(hostHint?.name ?? '')}
                    disabled={suppressAutoCreateUntilLinked}
                    className="btn btn-primary bg-teal-600 enabled:hover:bg-teal-700 focus-visible:ring-teal-500"
                  >
                    Neu erstellen
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
